const TOKEN_KEY = 'token'
const PERMISSIONS_KEY = 'permissions'
const LAST_ACTIVITY_KEY = 'adminLastActivity'
const SESSION_EVENT_KEY = 'adminSessionEvent'
const IDLE_TIMEOUT = 30 * 60 * 1000
const HEARTBEAT_INTERVAL = 5 * 60 * 1000
const ACTIVITY_THROTTLE = 1000
const ACTIVITY_EVENTS = ['click', 'keydown', 'pointerdown', 'touchstart', 'scroll']

let heartbeatTimer = null
let idleTimer = null
let lastRecordedActivity = 0
let clearing = false
let channel = null

function decodePayload(token) {
  try {
    const body = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = body.padEnd(Math.ceil(body.length / 4) * 4, '=')
    return JSON.parse(decodeURIComponent(atob(padded).split('').map(char =>
      `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
    ).join('')))
  } catch (error) {
    return null
  }
}

export function isValidAdminToken(token = getToken()) {
  if (!token || typeof token !== 'string' || token.split('.').length !== 3) return false
  const payload = decodePayload(token)
  const now = Math.floor(Date.now() / 1000)
  return !!(payload && payload.type === 'admin' && Number(payload.exp) > now)
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || ''
}

export function getPermissions() {
  const raw = localStorage.getItem(PERMISSIONS_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(String).map(item => item.trim()).filter(Boolean)
  } catch (error) {
    // Supports the comma-separated permissions returned by older APIs.
  }
  return raw.split(',').map(item => item.trim()).filter(Boolean)
}

export function hasPermission(permission) {
  return getPermissions().includes(permission)
}

function broadcast(type) {
  const message = { type, at: Date.now() }
  localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify(message))
  if (channel) channel.postMessage(message)
}

export function saveSession(data = {}) {
  if (!isValidAdminToken(data.token)) throw new Error('登录凭证无效或已过期')
  localStorage.setItem(TOKEN_KEY, data.token)
  localStorage.setItem(PERMISSIONS_KEY, Array.isArray(data.permissions)
    ? JSON.stringify(data.permissions)
    : String(data.permissions || ''))
  localStorage.setItem('adminId', String(data.id == null ? '' : data.id))
  localStorage.setItem('adminName', data.adminName || data.username || '管理员')
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
  broadcast('login')
}

export function clearSession({ notify = true } = {}) {
  if (clearing) return
  clearing = true
  ;[TOKEN_KEY, PERMISSIONS_KEY, LAST_ACTIVITY_KEY, 'adminId', 'adminName'].forEach(key => localStorage.removeItem(key))
  if (notify) broadcast('logout')
  window.dispatchEvent(new CustomEvent('admin-session-cleared'))
  clearing = false
}

export async function logout() {
  const token = getToken()
  try {
    if (token) {
      await fetch('/api/admin/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true
      })
    }
  } catch (error) {
    // Local cleanup must still happen if the server is unreachable.
  } finally {
    clearSession()
  }
}

function recordActivity(event) {
  if (event && !event.isTrusted) return
  if (!isValidAdminToken()) return
  const now = Date.now()
  if (now - lastRecordedActivity < ACTIVITY_THROTTLE) return
  lastRecordedActivity = now
  localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
}

async function heartbeat() {
  const token = getToken()
  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0)
  if (!isValidAdminToken(token) || Date.now() - lastActivity >= HEARTBEAT_INTERVAL) return
  try {
    const response = await fetch('/api/admin/auth/heartbeat', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    })
    if (response.status === 401 || response.status === 403) {
      clearSession()
      return
    }
    const result = await response.json()
    const refreshedToken = result && result.data && (result.data.token || result.data)
    if (typeof refreshedToken === 'string' && isValidAdminToken(refreshedToken) && refreshedToken !== token) {
      localStorage.setItem(TOKEN_KEY, refreshedToken)
      broadcast('refresh')
    }
  } catch (error) {
    // A transient heartbeat failure should not destroy a still-valid session.
  }
}

function checkIdle() {
  if (!getToken()) return
  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0)
  if (!isValidAdminToken() || !lastActivity || Date.now() - lastActivity >= IDLE_TIMEOUT) {
    clearSession()
  }
}

function handleSessionMessage(message) {
  if (!message || !message.type) return
  if (message.type === 'logout') {
    clearSession({ notify: false })
  } else {
    window.dispatchEvent(new CustomEvent('admin-session-updated'))
  }
}

export function initSession() {
  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel('admin-session')
    channel.onmessage = event => handleSessionMessage(event.data)
  }
  window.addEventListener('storage', event => {
    if (event.key === SESSION_EVENT_KEY && event.newValue) {
      try { handleSessionMessage(JSON.parse(event.newValue)) } catch (error) { /* ignore */ }
    }
  })
  ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, recordActivity, { passive: true }))
  window.addEventListener('focus', checkIdle)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) checkIdle()
  })
  checkIdle()
  idleTimer = window.setInterval(checkIdle, 30 * 1000)
  heartbeatTimer = window.setInterval(heartbeat, HEARTBEAT_INTERVAL)
}

export function stopSession() {
  window.clearInterval(idleTimer)
  window.clearInterval(heartbeatTimer)
  if (channel) channel.close()
}
